import {
	UserPropertyLevel,
	userPropertyLevelDisplayNames,
	UserRole,
} from '../domain/my-lqa/user-settings';
import { z } from 'zod';

const userPropertySchema = z.object({
	id: z.number(),
	name: z.string(),
	brandId: z.number().optional(),
	brandName: z.string().optional(),
	clientId: z.number().optional(),
	clientName: z.string().optional(),
	departmentId: z.number().optional(),
	departmentName: z.string().optional(),
	availableDepartmentIds: z.array(z.number()).optional(),
});

const userFormFieldsSchema = z.object({
	client: z.object({ id: z.number(), name: z.string() }).nullable(),
	firstName: z.string().min(1, 'First name is required'),
	lastName: z.string().min(1, 'Last name is required'),
	workEmail: z.string().email('Valid email is required'),
	personalEmail: z
		.string()
		.email('Must be a valid email')
		.optional()
		.or(z.literal('')),
	phoneNumber: z.string().optional(),
	jobTitle: z.string().optional(),
	role: z.enum(UserRole),
	isUserManagementAllowed: z.boolean().nullable(),
	canEnterBlockoutDates: z.boolean().nullable(),
	hasAppAccess: z.boolean(),
	licensingHotel: z.object({ id: z.number(), name: z.string() }).nullable(),
	hasLicensingHotelSeat: z.boolean().nullable(),
	notifyNewAssessment: z.boolean(),
	notifyAdditionalAssessment: z.boolean(),
	notifyCompetitiveSetUpdates: z.boolean(),
	propertyLevel: z.enum(UserPropertyLevel).nullable(),
	properties: z.array(userPropertySchema),
	hotels: z.array(userPropertySchema),
	departments: z.array(userPropertySchema),
});

type UserFormFields = z.infer<typeof userFormFieldsSchema>;
type UserPropertyValue = z.infer<typeof userPropertySchema>;

const DEPARTMENT_LEVELS: UserPropertyLevel[] = [
	UserPropertyLevel.department,
	UserPropertyLevel.subDepartment,
];

function addIssue(ctx: z.RefinementCtx, path: string, message: string): void {
	ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
}

function validatePropertySelection(
	values: UserFormFields,
	ctx: z.RefinementCtx,
	levelName: string
): void {
	if (values.properties.length > 0) {
		return;
	}

	addIssue(
		ctx,
		'properties',
		values.propertyLevel === UserPropertyLevel.client
			? 'Select at least one client'
			: `Add at least one ${levelName}`
	);
}

function findUnavailableDepartments(
	hotel: UserPropertyValue,
	departments: UserPropertyValue[]
): UserPropertyValue[] {
	if (!hotel.availableDepartmentIds) {
		return [];
	}

	const availableIds = new Set(hotel.availableDepartmentIds);
	return departments.filter(
		(department) => !availableIds.has(department.departmentId ?? department.id)
	);
}

function validateDepartmentSelection(
	values: UserFormFields,
	ctx: z.RefinementCtx,
	levelName: string
): void {
	if (values.hotels.length === 0) {
		addIssue(ctx, 'hotels', 'Select at least one hotel');
	}

	if (values.departments.length === 0) {
		addIssue(ctx, 'departments', `Add at least one ${levelName}`);
	}

	for (const hotel of values.hotels) {
		for (const department of findUnavailableDepartments(
			hotel,
			values.departments
		)) {
			addIssue(
				ctx,
				'departments',
				`${hotel.name} does not have a ${department.name} ${levelName}. Remove it from the selection.`
			);
		}
	}
}

function validatePermission(
	ctx: z.RefinementCtx,
	field: string,
	value: boolean | null,
	isManagerRole: boolean
): void {
	if (isManagerRole && value === null) {
		addIssue(ctx, field, 'Select a permission');
		return;
	}

	if (!isManagerRole && value !== null) {
		addIssue(ctx, field, 'Permissions are available for managers only');
	}
}

function validatePermissions(
	values: UserFormFields,
	ctx: z.RefinementCtx
): void {
	const isManagerRole = values.role === UserRole.manager;

	validatePermission(
		ctx,
		'isUserManagementAllowed',
		values.isUserManagementAllowed,
		isManagerRole
	);
	validatePermission(
		ctx,
		'canEnterBlockoutDates',
		values.canEnterBlockoutDates,
		isManagerRole
	);
}

function validateClientAccess(
	values: UserFormFields,
	ctx: z.RefinementCtx
): void {
	const clientId = values.client?.id;

	if (
		clientId !== undefined &&
		!values.properties.some((property) => property.id === clientId)
	) {
		addIssue(
			ctx,
			'properties',
			'The selected client must be included in client access'
		);
	}
}

function validateLicensingHotel(
	values: UserFormFields,
	ctx: z.RefinementCtx,
	propertyLevel: UserPropertyLevel
): void {
	if (!values.hasAppAccess || !values.licensingHotel) {
		return;
	}

	const accessibleHotels = DEPARTMENT_LEVELS.includes(propertyLevel)
		? values.hotels
		: values.properties;

	if (
		!accessibleHotels.some((hotel) => hotel.id === values.licensingHotel?.id)
	) {
		addIssue(
			ctx,
			'licensingHotel',
			'Select a licensing hotel the user has access to'
		);
		return;
	}

	if (values.hasLicensingHotelSeat === false) {
		addIssue(
			ctx,
			'licensingHotel',
			`${values.licensingHotel.name} has no myLQA seat available. Pick another hotel or add seats to its contract.`
		);
	}
}

export const userFormSchema = userFormFieldsSchema.superRefine(
	(values, ctx) => {
		if (values.client === null) {
			addIssue(ctx, 'client', 'Select a client');
		}

		validatePermissions(values, ctx);

		if (values.propertyLevel === null) {
			addIssue(ctx, 'propertyLevel', 'Select a property level');
			return;
		}

		const levelName =
			userPropertyLevelDisplayNames[values.propertyLevel].toLowerCase();

		if (DEPARTMENT_LEVELS.includes(values.propertyLevel)) {
			validateDepartmentSelection(values, ctx, levelName);
			validateLicensingHotel(values, ctx, values.propertyLevel);
			return;
		}

		validatePropertySelection(values, ctx, levelName);

		if (values.propertyLevel === UserPropertyLevel.client) {
			validateClientAccess(values, ctx);
			return;
		}

		validateLicensingHotel(values, ctx, values.propertyLevel);
	}
);

export type UserFormValues = z.infer<typeof userFormSchema>;
